import axios, { AxiosError } from 'axios';
import { env } from '../../config/env';
import { safeRequestFailure } from '../dnmFailure';
import type { PublicTrackingResponse } from '../types';

/**
 * Public tracking (#294, Contract dnm-work#150): GET /public/track/{code} needs no login.
 * A plain client on the same base URL, deliberately NOT the shared `api` instance: that one
 * attaches the Cognito token and signs the user out on a 401, neither of which belongs on a
 * page anyone with the link can open.
 */
export const publicApi = axios.create({ baseURL: env.apiBaseUrl, timeout: 15_000 });

export type PublicTrackingResult =
  | { kind: 'open'; tracking: PublicTrackingResponse }
  | { kind: 'closed' | 'notFound'; supportPhone: string };

function supportPhoneOf(error: AxiosError): string {
  const body = error.response?.data;
  const phone = body && typeof body === 'object' ? (body as Record<string, unknown>).supportPhone : null;
  return typeof phone === 'string' ? phone : '';
}

export async function getPublicTracking(code: string): Promise<PublicTrackingResult> {
  try {
    const { data } = await publicApi.get<PublicTrackingResponse>(`/public/track/${encodeURIComponent(code)}`);
    return { kind: 'open', tracking: data };
  } catch (e) {
    if (!axios.isAxiosError(e)) throw e;
    const status = e.response?.status;
    if (status === 410) return { kind: 'closed', supportPhone: supportPhoneOf(e) };
    if (status === 404) return { kind: 'notFound', supportPhone: supportPhoneOf(e) };
    throw safeRequestFailure(e);
  }
}
